import React, { useEffect, useMemo, useState } from "react";
import PermissionGrid from "../components/PermissionGrid";
import { useToast } from "../components/Toast";
import { useBuilding } from "../context/BuildingContext";
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { FaArrowLeft, FaLock, FaSave, FaBuilding } from "react-icons/fa";

export default function PermisosUsuario({ setSelected }) {
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const scopeId = selectedBuilding?.id || "global";

  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Las keys se persisten en Firestore con los nombres historicos
  // ("Ver Expensas", "Pagar Expensas"). Para mostrar la nueva nomenclatura
  // ("Gastos Comunes") sin romper permisos guardados, mapeamos key -> label
  // visible a traves de FEATURE_LABELS.
  const FEATURES = useMemo(
    () => [
      "Ver Calendario",
      "Ver Resultados",
      "Ver Marcaciones",
      "Ver Expensas",
      "Pagar Expensas",
      "Reservar Amenities",
      "Ver Proveedores",
      "Contactar Administración",
      "Ver Reglamento",
      "Reportar Incidencias",
      "Ver Mis Unidades",
    ],
    []
  );

  const FEATURE_LABELS = useMemo(
    () => ({
      "Ver Expensas": "Ver Gastos Comunes",
      "Pagar Expensas": "Pagar Gastos Comunes",
    }),
    []
  );

  const COLUMNS = useMemo(
    () => [
      { key: "comision", label: "Prop. Comisión" },
      { key: "propietario", label: "Propietario" },
      { key: "inquilino", label: "Inquilino" },
    ],
    []
  );

  const defaultPerms = useMemo(() => {
    const base = {};
    FEATURES.forEach((f) => {
      base[f] = { comision: true, propietario: true, inquilino: true };
    });
    base["Pagar Expensas"] = { comision: true, propietario: true, inquilino: false };
    return base;
  }, [FEATURES]);

  useEffect(() => {
    async function loadPerms() {
      setLoading(true);
      try {
        const ref = doc(db, "role_permissions", scopeId);
        const d = await getDoc(ref);
        let data = defaultPerms;
        const fromDb = d.exists() ? d.data()?.permisos : null;
        if (fromDb) {
          data = { ...defaultPerms, ...fromDb };
          for (const f of Object.keys(data)) {
            const row = data[f] || {};
            data[f] = {
              comision: !!row.comision,
              propietario: !!row.propietario,
              inquilino: !!row.inquilino,
            };
          }
        }
        setValues(data);
      } catch (e) {
        console.error("Error cargando permisos roles:", e);
        setValues(defaultPerms);
      } finally {
        setLoading(false);
      }
    }
    loadPerms();
  }, [scopeId, defaultPerms]);

  const onToggle = (featureKey, columnKey, checked) => {
    setValues((prev) => ({
      ...prev,
      [featureKey]: { ...prev[featureKey], [columnKey]: checked },
    }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const toSave = {};
      FEATURES.forEach((f) => {
        const row = values?.[f] || {};
        toSave[f] = {
          comision: !!row.comision,
          propietario: !!row.propietario,
          inquilino: !!row.inquilino,
        };
      });
      const ref = doc(db, "role_permissions", scopeId);
      await setDoc(ref, { permisos: toSave, updatedAt: serverTimestamp() }, { merge: true });
      showToast("Permisos guardados correctamente.", "success");
    } catch (e) {
      console.error(e);
      showToast("No se pudieron guardar los permisos.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header con botón volver */}
      <div style={styles.headerRow}>
        <button
          style={styles.backBtn}
          onClick={() => setSelected("homesuperadmin")}
          onMouseEnter={(e) => e.currentTarget.style.background = "#f0f7ff"}
          onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
        >
          <FaArrowLeft size={14} />
          <span>Volver a Panel Admin</span>
        </button>
      </div>

      {/* Card principal */}
      <div style={styles.headerCard}>
        <div style={styles.headerTop}>
          <div style={styles.iconBox}>
            <FaLock size={20} color="#0080ff" />
          </div>
          <div style={styles.headerInfo}>
            <h1 style={styles.title}>Permisos por Usuario</h1>
            <p style={styles.subtitle}>Definí qué funciones puede realizar cada tipo de usuario en la plataforma</p>
          </div>
        </div>
      </div>

      {/* Barra de acciones */}
      <div style={styles.actionsCard}>
        <div style={styles.scopeBadge}>
          <FaBuilding size={14} color="#6b7280" />
          <span style={styles.scopeLabel}>Ámbito:</span>
          <strong style={styles.scopeValue}>{selectedBuilding?.name || "Global"}</strong>
        </div>
        <button 
          onClick={onSave} 
          style={styles.saveBtn} 
          disabled={saving || loading}
          onMouseEnter={(e) => !saving && (e.currentTarget.style.background = "#006dd1")}
          onMouseLeave={(e) => !saving && (e.currentTarget.style.background = "#0080ff")}
        >
          <FaSave size={14} />
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>

      {/* Grid de permisos */}
      <div style={styles.gridCard}>
        {loading ? (
          <div style={styles.loader}>
            <div style={styles.spinner}></div>
            <span>Cargando permisos...</span>
          </div>
        ) : (
          <PermissionGrid
            features={FEATURES}
            featureLabels={FEATURE_LABELS}
            columns={COLUMNS}
            values={values}
            onToggle={onToggle}
          />
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: 24,
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  headerRow: {
    marginBottom: 16,
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 16px",
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 400,
    color: "#374151",
    transition: "all 0.2s ease",
  },
  headerCard: {
    backgroundColor: "#fff",
    padding: "24px",
    borderRadius: 12,
    marginBottom: 16,
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  iconBox: {
    width: 52,
    height: 52,
    borderRadius: 12,
    backgroundColor: "#f0f7ff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: 22,
    fontWeight: 400,
    margin: 0,
    color: "#1a1a1a",
  },
  subtitle: {
    fontSize: 14,
    color: "#6b7280",
    marginTop: 4,
    marginBottom: 0,
  },
  actionsCard: {
    backgroundColor: "#fff",
    padding: "16px 20px",
    borderRadius: 12,
    marginBottom: 16,
    border: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  scopeBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
  },
  scopeLabel: {
    fontSize: 14,
    color: "#6b7280",
  },
  scopeValue: {
    fontSize: 14,
    color: "#1a1a1a",
  },
  saveBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    border: "none",
    backgroundColor: "#0080ff",
    color: "#fff",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 400,
    fontSize: 14,
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(0,128,255,0.25)",
  },
  gridCard: {
    backgroundColor: "#fff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  loader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: 40,
    color: "#6b7280",
    fontSize: 14,
  },
  spinner: {
    width: 24,
    height: 24,
    border: "3px solid #e5e7eb",
    borderTop: "3px solid #0080ff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};
